import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { Button, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Params } from "react-router";
import { useParams } from "react-router";
import { ErrorDialog } from "@/components/common/ErrorDialog";
import {
  downloadOrGenerateDeliveryReport,
  regenerateDeliveryReport,
} from "@/utils/delivery-report";

type PdfDialog = "prerequisites" | "generating" | "error" | null;
type LoadingAction = "download" | "regenerate" | null;

// ready: delivery is completed and has before/after photos required to generate the PDF
function PdfSection({ ready = false }: { ready: boolean }) {
  const { t } = useTranslation();
  const { contract: id } = useParams<Params>();
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);
  const [dialog, setDialog] = useState<PdfDialog>(null);
  const isLoading = loadingAction !== null;

  const handleDownloadRapport = async () => {
    if (!id) {
      return;
    }

    if (!ready) {
      setDialog("prerequisites");
      return;
    }

    const deliveryId = Number(id);
    if (Number.isNaN(deliveryId)) {
      return;
    }

    setLoadingAction("download");
    try {
      const outcome = await downloadOrGenerateDeliveryReport(id, true);
      if (outcome === "generating") {
        setDialog("generating");
      } else if (outcome === "error") {
        setDialog("error");
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRegenerateRapport = async () => {
    if (!id) {
      return;
    }

    const deliveryId = Number(id);
    if (Number.isNaN(deliveryId)) {
      return;
    }

    setLoadingAction("regenerate");
    try {
      const outcome = await regenerateDeliveryReport(id);
      if (outcome === "generating") {
        setDialog("generating");
      } else if (outcome === "error") {
        setDialog("error");
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const closeDialog = () => setDialog(null);

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Button
          color="info"
          disabled={isLoading}
          onClick={() => {
            void handleDownloadRapport();
          }}
          startIcon={<CloudDownloadIcon />}
          variant="contained"
        >
          {loadingAction === "download"
            ? t("common.shared.downloading")
            : t("common.shared.download-pdf")}
        </Button>
        <Button
          color="info"
          disabled={isLoading}
          onClick={() => {
            void handleRegenerateRapport();
          }}
          startIcon={<AutorenewIcon />}
          variant="outlined"
        >
          {loadingAction === "regenerate"
            ? t("delivery.pdf-report.regenerating")
            : t("delivery.pdf-report.regenerate")}
        </Button>
      </Stack>
      <ErrorDialog
        description={t("delivery.pdf-report.not-ready.description")}
        onClose={closeDialog}
        open={dialog === "prerequisites"}
        retryText={t("common.confirm")}
        title={t("delivery.pdf-report.not-ready.title")}
      />
      <ErrorDialog
        description={t("delivery.pdf-report.generating.description")}
        instruction={t("delivery.pdf-report.generating.instruction")}
        onClose={closeDialog}
        open={dialog === "generating"}
        retryText={t("common.confirm")}
        title={t("delivery.pdf-report.generating.title")}
      />
      <ErrorDialog
        description={t("delivery.pdf-report.error.description")}
        instruction={t("delivery.pdf-report.error.instruction")}
        onClose={closeDialog}
        open={dialog === "error"}
        retryText={t("common.confirm")}
        title={t("delivery.pdf-report.error.title")}
      />
    </>
  );
}

export default PdfSection;
